import { zodResolver } from '@hookform/resolvers/zod';
import { updatePassword } from 'api/user';
import Button from 'components/form/Button';
import { InputWithStyle } from 'components/form/Input';
import { TabContent } from 'components/layouts/TabComponent';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

interface PasswordProps {
  currentTab: string;
}

const PasswordSchema = z
  .object({
    oldPassword: z.string().min(8, 'Password is too short'),
    newPassword: z.string().min(8, 'Password is too short'),
    confirmPassword: z.string().min(8, 'Password is too short'),
  })
  .refine(
    (data) => {
      let check = true;
      if (data.oldPassword === data.newPassword) return (check = false);
      if (data.newPassword !== data.confirmPassword) return (check = false);

      return check;
    },
    (data) => {
      const message =
        data.oldPassword === data.newPassword
          ? "old password and new password can't be the same"
          : 'password does not match';
      const path =
        data.oldPassword === data.newPassword
          ? 'newPassword'
          : 'confirmPassword';

      return {
        message,
        path: [path],
      };
    }
  );

type PasswordSchemaType = z.infer<typeof PasswordSchema>;

const PasswordTab = ({ currentTab }: PasswordProps) => {
  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<PasswordSchemaType>({
    resolver: zodResolver(PasswordSchema),
    mode: 'onTouched',
  });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const changeFunction = async (data: PasswordSchemaType) => {
    setLoading(true);
    try {
      const res = await updatePassword(data);
      if (res?.data) {
        toast.success('password changed');
        reset();
      }
    } catch (error: any) {
      const { errors } = error.response.data;
      errors?.map((err: { error: string }) => {
        return toast.error(err.error);
      });
      console.log(error);
    }
    setLoading(false);
  };

  const onSubmit = (data: PasswordSchemaType) => {
    changeFunction(data);
  };

  return (
    <TabContent tabId="tab3" currentTab={currentTab}>
      <form className="grid grid-cols-1 gap-y-4 py-3">
        <div className="grid grid-cols-1 gap-8 pb-5 md:grid-cols-2">
          <label className="space-y-2">
            <div className="flex items-center space-x-2">
              <p className="font-bold capitalize">Old password</p>
            </div>
            <InputWithStyle
              endIcon={
                <Button type="button" onClick={() => setShow(!show)}>
                  {show ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                      />
                    </svg>
                  )}
                </Button>
              }
              type={show ? 'text' : 'password'}
              name="oldPassword"
              placeholder="---"
              required
              control={control}
            />
          </label>
        </div>
        <div className="grid grid-cols-1 gap-8 pb-5 md:grid-cols-2">
          <label className="space-y-2">
            <div className="flex items-center space-x-2">
              <p className="font-bold capitalize">New password</p>
            </div>
            <InputWithStyle
              endIcon={
                <Button type="button" onClick={() => setShow(!show)}>
                  {show ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                      />
                    </svg>
                  )}
                </Button>
              }
              type={show ? 'text' : 'password'}
              name="newPassword"
              placeholder="---"
              required
              control={control}
            />
          </label>
          <label className="space-y-2">
            <div className="flex items-center space-x-2">
              <p className="font-bold capitalize">Confirm password</p>
            </div>
            <InputWithStyle
              endIcon={
                <Button type="button" onClick={() => setShow(!show)}>
                  {show ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                      />
                    </svg>
                  )}
                </Button>
              }
              type={show ? 'text' : 'password'}
              name="confirmPassword"
              placeholder="---"
              required
              control={control}
            />
          </label>
        </div>
        <div className="flex w-full justify-end">
          <Button
            className="rounded-md bg-blue-500 px-3 py-2 text-white"
            disabled={!isDirty || !isValid || loading}
            loading={loading}
            onClick={handleSubmit(onSubmit)}
          >
            update
          </Button>
        </div>
      </form>
    </TabContent>
  );
};

export default PasswordTab;
